import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useState } from 'react';

/**
 * A custom hook for managing password visibility state with convenient helpers.
 *
 * @param initialVisible - Whether password should start visible (defaults to false)
 * @returns An object with visibility state and helpful computed values
 *
 * @example
 * ```tsx
 * const password = usePasswordVisibility();
 *
 * return (
 *   <TextField
 *     type={password.inputType}
 *     rightAdornment={
 *       <button
 *         onClick={password.toggle}
 *         aria-label={password.ariaLabel}
 *       >
 *         <FontAwesomeIcon icon={password.icon} />
 *       </button>
 *     }
 *   />
 * );
 * ```
 */
export const usePasswordVisibility = (initialVisible = false) => {
  const [isVisible, setIsVisible] = useState(initialVisible);

  const toggle = useCallback(() => {
    setIsVisible((prev) => !prev);
  }, []);

  const show = useCallback(() => {
    setIsVisible(true);
  }, []);

  const hide = useCallback(() => {
    setIsVisible(false);
  }, []);

  return {
    isVisible,
    toggle,
    show,
    hide,
    set: setIsVisible,

    inputType: isVisible ? 'text' : 'password',
    icon: isVisible ? faEyeSlash : faEye,
    ariaLabel: isVisible ? 'Hide password' : 'Show password',
  } as const;
};

export type PasswordVisibilityReturn = ReturnType<typeof usePasswordVisibility>;
